import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import helpService, { HelpItem } from "../../../../Axios/services/help.service";

interface AuthState {
  token: string;
  me: any;
}

interface RootState {
  auth: AuthState;
}

export const useHelpItems = () => {
  const [helpItems, setHelpItems] = useState<HelpItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get authentication state from Redux store
  const { token, me } = useSelector((state: RootState) => state.auth);
  
  // Check if we have both token and user data
  const hasToken = Boolean(token && (token.length > 0 || localStorage.getItem('token')));
  const hasUserData = Boolean(me);
  const isAuthenticated = hasToken && hasUserData;

  const loadHelpItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await helpService.getAll();
      
      // Sort items by Order__c field (nulls last)
      const sortedItems = items.sort((a, b) => {
        if (!a.Order__c && !b.Order__c) return 0;
        if (!a.Order__c) return 1;
        if (!b.Order__c) return -1;
        return a.Order__c - b.Order__c;
      });
      
      setHelpItems(sortedItems);
    } catch (err: any) {
      // Handle specific authentication errors
      if (err.response?.status === 401) {
        setError('Authentication required. Please log in to view help items.');
      } else {
        setError(err.message || 'Failed to load help items');
      }
      console.error('Error loading help items:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we have token but no user data yet, keep loading
    if (hasToken && !hasUserData) {
      setLoading(true);
      setError(null);
      return;
    }
    
    // If fully authenticated (token + user data), load help items
    if (isAuthenticated) {
      loadHelpItems();
    } else {
      // If no token at all, stop loading
      setLoading(false);
      setError(null);
    }
  }, [hasToken, hasUserData, isAuthenticated, loadHelpItems]);

  const retryLoad = () => {
    if (isAuthenticated) {
      loadHelpItems();
    }
  };

  return {
    helpItems,
    loading,
    error,
    retryLoad,
    isAuthenticated,
    hasUserData
  };
};